import { useState, useEffect } from "react";
import { Loader2, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { capture } from "@/lib/analytics";
import { signInWithGoogle } from "@/lib/auth";
import { useSubscription } from "@/hooks/use-subscription";
import { BrandStylesModal } from "@/components/BrandStylesModal";
import { PricingModal } from "@/components/PricingModal";
import type { Session } from "@/hooks/use-session";

const EXAMPLE_PROMPTS = [
  "A pulsating neon wave that flows like water in dark space",
  "A geometric flower blooming and rotating in pastel colors",
  "A glowing particle system forming a star constellation",
  "An abstract city skyline with animated light beams",
];

const PENDING_PROMPT_KEY = "vectoranimation_pending_prompt";

interface PromptFormProps {
  session: Session | null | undefined;
}

export function PromptForm({ session }: PromptFormProps) {
  const [prompt, setPrompt] = useState("");
  const [queued, setQueued] = useState(false);
  const [showBrandStyles, setShowBrandStyles] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const { subscription, loading: isLoadingSubscription, refresh: refreshSub } = useSubscription(session?.user?.id);

  // After OAuth redirect, restore the prompt the user typed before sign-in
  useEffect(() => {
    const pending = sessionStorage.getItem(PENDING_PROMPT_KEY);
    if (pending) {
      setPrompt(pending);
      sessionStorage.removeItem(PENDING_PROMPT_KEY);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    const trimmed = prompt.trim();

    // Not signed in — save prompt and redirect to Google
    if (!session) {
      sessionStorage.setItem(PENDING_PROMPT_KEY, trimmed);
      signInWithGoogle();
      return;
    }

    capture("generate_started", { prompt_length: trimmed.length });

    const startedAt = Date.now();
    try {
      const res = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: trimmed }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Unknown error" }));
        const errorMessage = (err as { error?: string }).error ?? "Generation failed";
        
        if (res.status === 403 && errorMessage.includes("limit")) {
          setShowPricing(true);
          return;
        }
        
        throw new Error(errorMessage);
      }

      const { job_id } = (await res.json()) as { job_id: string };
      capture("generate_queued", {
        job_id,
        prompt_length: trimmed.length,
        duration_ms: Date.now() - startedAt,
      });
      setPrompt("");
      setQueued(true);
      setTimeout(() => setQueued(false), 1500);
      void refreshSub();
      toast({
        title: "Generation queued",
        description: "Your animation is generating and will appear in the gallery.",
      });
    } catch (err) {
      capture("generate_failed", {
        error: err instanceof Error ? err.message : String(err),
        prompt_length: trimmed.length,
        duration_ms: Date.now() - startedAt,
      });
      toast({
        variant: "destructive",
        title: "Generation failed",
        description: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      // no-op
    }
  };

  const handleExampleClick = (example: string) => {
    setPrompt(example);
    capture("example_prompt_clicked", { prompt: example });
  };

  const isSessionLoading = session === undefined;
  const isSignedOut = session === null;

  return (
    <div className="w-full max-w-4xl mx-auto space-y-12">
      <PricingModal 
        open={showPricing} 
        onClose={() => setShowPricing(false)} 
        currentPlan={subscription?.plan} 
      />

      {subscription?.plan === "enterprise" && (
        <BrandStylesModal open={showBrandStyles} onClose={() => setShowBrandStyles(false)} />
      )}

      <div className="text-center space-y-6">
        <h1 className="text-5xl font-bold tracking-tight text-foreground sm:text-7xl">
          Create animated SVGs
        </h1>
        <p className="text-muted-foreground text-xl max-w-2xl mx-auto">
          Turn a short description into a looping vector animation.
        </p>
        
        {session && subscription && !isLoadingSubscription && (
          <div className="flex items-center justify-center gap-3 pt-4">
            <div className="px-4 py-1.5 rounded-full border border-border/10 bg-muted/50 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
              {subscription.plan} plan: {subscription.generationCount} / {subscription.limit} used
            </div>
            {subscription.plan !== "enterprise" && (
              <button
                className="text-[11px] font-bold uppercase tracking-widest text-primary hover:opacity-80 transition-opacity"
                onClick={() => setShowPricing(true)}
              >
                Upgrade
              </button>
            )}
          </div>
        )}
      </div>

      <div className="relative group">
        <form 
          onSubmit={handleSubmit} 
          className="relative rounded-[1.5rem] bg-card border border-border/40 p-6 shadow-2xl overflow-hidden focus-within:border-primary/30 transition-all duration-300"
        >
          <div className="space-y-4">
            <Textarea
              id="prompt"
              placeholder="Describe the animation… e.g. 'A glowing aurora borealis'"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="min-h-[140px] text-xl border-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 resize-none placeholder:text-muted-foreground/50 p-0 shadow-none outline-none"
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleSubmit(e as unknown as React.FormEvent);
                }
              }}
            />
            
            <div className="flex items-center justify-between pt-4 border-t border-border/10">
              <div className="flex items-center gap-3">
                {subscription?.plan === "enterprise" && (
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold bg-muted hover:bg-muted/80 transition-colors"
                    onClick={() => setShowBrandStyles(true)}
                  >
                    <Palette className="h-3.5 w-3.5" />
                    Styles
                  </button>
                )}
              </div>

              <Button
                type="submit"
                disabled={!prompt.trim() || isSessionLoading}
                className="rounded-full px-8 h-12 text-sm font-bold bg-foreground text-background hover:bg-foreground/90 transition-all active:scale-95"
              >
                {isSessionLoading ? (
                  <Loader2 className="animate-spin" />
                ) : queued ? (
                  "Queued..."
                ) : isSignedOut ? (
                  "Sign in & Generate"
                ) : (
                  "Generate Animation"
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>

      <div className="space-y-4">
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground text-center">Try an example:</p>
        <div className="flex flex-wrap gap-2.5 justify-center">
          {EXAMPLE_PROMPTS.map((example) => (
            <button
              key={example}
              onClick={() => handleExampleClick(example)}
              className="text-sm px-5 py-2.5 rounded-full border border-border/10 bg-muted/30 hover:bg-muted/50 hover:border-border/40 transition-all text-muted-foreground hover:text-foreground"
            >
              {example}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
